import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"

/*
 * Autosuggest list: the results can be walked with the arrow keys and taken with Enter or Tab.
 * The caller does the searching: listen to @input (the typed text, debounced) and feed the matches
 * back through :results; @onSelect gets the picked item.
 *
 * The class a caller passes (`class="form-control"`) lands on the input, not on the wrapper.
 * Default markup classes (vue3-*) are kept from the "vue3-autocomplete" package this replaced, e.g.:
 *
 * .vue3-autocomplete-container { flex: 1; }
 * .vue3-results-container { position: absolute; z-index: 100; min-width: 20rem; padding: 0.5rem 0;
 *   background: #fff; border: 1px solid rgba(0, 0, 0, 0.15); border-radius: 0.25rem; }
 * .vue3-results-item { padding: 0.25rem 1.5rem; cursor: pointer; }
 * .vue3-results-item:hover { background-color: #f8f9fa; }
 * .vue3-results-item.active { background-color: #e9ecef; }
 */

// module scope, so every instance gets its own list id
let instanceCount = 0

export default {
  name: "Autocomplete",
  inheritAttrs: false,
  props: {
    debounce: { type: Number, default: 0 },
    inputClass: { type: Array, default: () => [] },
    useHtmlForResults: { type: Boolean, default: false },
    max: { type: Number, default: 10 },
    placeholder: { type: String, default: "" },
    results: { type: Array, default: () => [] },
    resultsContainerClass: { type: Array, default: () => [] },
    resultsItemClass: { type: Array, default: () => [] },
    displayItem: {
      type: Function,
      default: (item) => (typeof item === "string" ? item : item.name),
    },
  },
  emits: ["input", "onSelect"],
  template: `
    <div class="vue3-autocomplete-container">
      <input
        ref="inputEl"
        type="text"
        role="combobox"
        autocomplete="off"
        aria-autocomplete="list"
        v-bind="$attrs"
        v-model="searchText"
        :placeholder="placeholder"
        :class="getInputClass"
        :aria-expanded="shouldShowResults ? 'true' : 'false'"
        :aria-controls="listId"
        :aria-activedescendant="activeDescendantId"
        @input="handleInput"
        @focus="displayResults"
        @blur="hideResults"
        @keydown="handleKeydown"
      />

      <div
        v-if="shouldShowResults"
        ref="resultsContainerEl"
        :id="listId"
        role="listbox"
        :style="{ width: inputWidth + 'px' }"
        :class="getResultsContainerClass"
      >
        <template v-if="useHtmlForResults">
          <div
            v-for="(item, index) in filteredResults"
            :key="index"
            :id="optionId(index)"
            :data-index="index"
            role="option"
            :aria-selected="index === highlightedIndex ? 'true' : 'false'"
            :class="[getResultsItemClass, { active: index === highlightedIndex }]"
            v-html="displayItem(item)"
            @mousemove="highlightedIndex = index"
            @click="selectItem(item)"
            @mousedown.prevent
          ></div>
        </template>
        <template v-else>
          <div
            v-for="(item, index) in filteredResults"
            :key="index"
            :id="optionId(index)"
            :data-index="index"
            role="option"
            :aria-selected="index === highlightedIndex ? 'true' : 'false'"
            :class="[getResultsItemClass, { active: index === highlightedIndex }]"
            @mousemove="highlightedIndex = index"
            @click="selectItem(item)"
            @mousedown.prevent
          >
            {{ displayItem(item) }}
          </div>
        </template>
      </div>
    </div>
  `,
  setup(props, { emit, expose }) {
    const listId = `autocomplete-list-${++instanceCount}`
    const inputEl = ref(null)
    const resultsContainerEl = ref(null)
    const inputWidth = ref(0)
    const searchText = ref("")
    const showResults = ref(true)
    const highlightedIndex = ref(-1)
    let debounceTimeout

    const optionId = (index) => `${listId}-option-${index}`

    const filteredResults = computed(() => props.results.slice(0, props.max))

    const shouldShowResults = computed(() => showResults.value && filteredResults.value.length > 0)

    const activeDescendantId = computed(() =>
      highlightedIndex.value >= 0 && shouldShowResults.value ? optionId(highlightedIndex.value) : null,
    )

    const getInputClass = computed(() => (props.inputClass.length > 0 ? props.inputClass : ["vue3-input"]))

    const getResultsContainerClass = computed(() =>
      props.resultsContainerClass.length > 0 ? props.resultsContainerClass : ["vue3-results-container"],
    )

    const getResultsItemClass = computed(() =>
      props.resultsItemClass.length > 0 ? props.resultsItemClass : ["vue3-results-item"],
    )

    const measure = () => {
      if (inputEl.value) {
        inputWidth.value = inputEl.value.offsetWidth - 2
      }
    }

    const setHighlighted = (index) => {
      highlightedIndex.value = index
      if (index < 0) return
      // the list is up to "max" entries long and may be scrolled, the highlight has to stay in view
      nextTick(() => {
        resultsContainerEl.value?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({ block: "nearest" })
      })
    }

    const moveHighlight = (step) => {
      const count = filteredResults.value.length
      if (count === 0) return
      showResults.value = true
      const current = highlightedIndex.value
      // down from "nothing picked" starts at the top, up starts at the bottom, then both wrap around
      setHighlighted(current < 0 ? (step > 0 ? 0 : count - 1) : (current + step + count) % count)
    }

    const selectItem = (item) => {
      // the picked entry becomes the text in the box, as in any other autosuggest
      if (item !== undefined && item !== null && !props.useHtmlForResults) {
        searchText.value = String(props.displayItem(item) ?? "")
      }
      showResults.value = false
      setHighlighted(-1)
      emit("onSelect", item)
    }

    // Triggered on input changes with a dynamic debounce
    const handleInput = (e) => {
      showResults.value = true
      setHighlighted(-1)
      clearTimeout(debounceTimeout)
      debounceTimeout = setTimeout(() => emit("input", e.target.value), props.debounce)
    }

    const handleKeydown = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        moveHighlight(1)
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        moveHighlight(-1)
      } else if (e.key === "Enter") {
        // Enter takes the highlighted entry; a single match counts as highlighted, so a fully typed
        // name can just be confirmed
        const index =
          highlightedIndex.value >= 0 ? highlightedIndex.value : filteredResults.value.length === 1 ? 0 : -1
        if (index >= 0) {
          e.preventDefault()
          selectItem(filteredResults.value[index])
        }
      } else if (e.key === "Tab") {
        if (shouldShowResults.value && highlightedIndex.value >= 0) {
          selectItem(filteredResults.value[highlightedIndex.value])
        }
      } else if (e.key === "Escape") {
        // the first Escape closes the list, the next one clears what was typed
        if (shouldShowResults.value) {
          showResults.value = false
          setHighlighted(-1)
        } else if (searchText.value !== "") {
          searchText.value = ""
          emit("input", "")
        }
      }
    }

    const displayResults = () => {
      measure()
      showResults.value = true
      setHighlighted(-1)
    }

    const hideResults = () => {
      showResults.value = false
      setHighlighted(-1)
    }

    // Manually update the displayed text in the input
    const setText = (value) => {
      searchText.value = value ?? ""
      setHighlighted(-1)
    }

    watch(
      () => props.results,
      () => setHighlighted(-1),
    )

    onMounted(measure)
    onBeforeUnmount(() => clearTimeout(debounceTimeout))

    // a parent reaches in through a template ref to empty the box
    expose({ setText })

    return {
      listId,
      inputEl,
      resultsContainerEl,
      inputWidth,
      searchText,
      highlightedIndex,
      optionId,
      filteredResults,
      shouldShowResults,
      activeDescendantId,
      getInputClass,
      getResultsContainerClass,
      getResultsItemClass,
      selectItem,
      handleInput,
      handleKeydown,
      displayResults,
      hideResults,
    }
  },
}
